import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';

import { StatCards } from '@/components/panel/panels';
import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, TextCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatRelative } from '@/lib/format';
import { formatTokens, formatUsd, formatUsdPrecise } from '@/lib/format-spend';

import { aiSpendQueryOptions } from './ai-spend-query';
import { AI_SPEND_WINDOW_OPTIONS, type AiSpendSearch } from './ai-spend-search';

const COLUMNS: readonly TableColumn[] = [
  { key: 'person', label: 'Person', width: '1.5fr' },
  { key: 'cost', label: 'Cost', width: '96px', align: 'right' },
  { key: 'share', label: 'Share of bill', width: '118px', align: 'right' },
  { key: 'calls', label: 'Calls', width: '72px', align: 'right' },
  { key: 'runs', label: 'Plan runs', width: '84px', align: 'right' },
  { key: 'perCall', label: 'Per call', width: '92px', align: 'right' },
  { key: 'topTask', label: 'Heaviest task', width: '1fr' },
  { key: 'last', label: 'Last used', width: '130px' },
  { key: 'open', label: '', width: '40px', align: 'right' },
];

const ACCESS_FILTER_OPTIONS = [
  { value: 'premium', label: 'Premium' },
  { value: 'free', label: 'Free' },
] as const;

function humanise(task: string): string {
  const words = task.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function percent(share: number | null): string {
  if (share === null) return '—';
  if (share > 0 && share < 0.001) return '< 0.1%';
  return `${String(Math.round(share * 1000) / 10)}%`;
}

export interface AiSpendScreenProps {
  readonly search: AiSpendSearch;
  readonly onSearchChange: (next: AiSpendSearch) => void;
  readonly onOpenUser: (userId: string) => void;
}

/**
 * Who the model bill belongs to.
 *
 * The window sits above the cards because it drives them; the search, the
 * access filter and the sort sit on the table because they only shape the
 * rows. The cards always describe the whole window, so a search for one person
 * never makes the bill look smaller than it is.
 */
export function AiSpendScreen({ search, onSearchChange, onOpenUser }: AiSpendScreenProps) {
  const { api } = useAuth();
  const query = useQuery(aiSpendQueryOptions(api, search));
  const data = query.data;
  const isSearching = search.search !== undefined;

  const rows: readonly TableRow[] = useMemo(() => {
    const bill = data?.totals.costUsd ?? null;
    return (data?.users ?? []).map((user, index) => ({
      id: user.userId,
      onOpen: () => {
        onOpenUser(user.userId);
      },
      cells: [
        <span key="person" className="flex min-w-0 items-center gap-2.5">
          {/* The position, not a monogram: where someone sits in the ranking
              is the fact this column exists for. A search result has no
              position worth showing. */}
          {isSearching ? null : (
            <span className="w-[18px] shrink-0 text-right font-mono text-xs text-muted-foreground">
              {index + 1}
            </span>
          )}
          <span className="flex min-w-0 flex-col gap-px">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-[13px] font-medium text-foreground">
                {user.displayName ?? 'Unnamed'}
              </span>
              {user.entitlement === 'premium' ? (
                <Badge tone="purple" size="sm">
                  premium
                </Badge>
              ) : null}
            </span>
            <span className="truncate font-mono text-xs text-muted-foreground">{user.phone}</span>
          </span>
        </span>,
        <TextCell
          key="cost"
          value={formatUsd(user.costUsd, user.costIsPartial)}
          mono
          align="right"
          weight="medium"
        />,
        <ShareCell
          key="share"
          share={user.costUsd === null || bill === null || bill === 0 ? null : user.costUsd / bill}
        />,
        <TextCell key="calls" value={user.calls.toLocaleString('en-IN')} mono align="right" />,
        <TextCell key="runs" value={user.planRuns.toLocaleString('en-IN')} mono align="right" />,
        <TextCell key="perCall" value={formatUsdPrecise(user.costPerCallUsd)} mono align="right" />,
        user.topTask === null ? (
          <TextCell key="topTask" value={null} />
        ) : (
          <BadgeCell key="topTask" label={humanise(user.topTask)} tone="mute" dot />
        ),
        <TextCell key="last" value={formatRelative(user.lastCallAt)} sub={formatDateTime(user.lastCallAt)} />,
        <span key="open" className="flex justify-end">
          <ChevronRight aria-hidden="true" className="size-4 text-input" />
        </span>,
      ],
    }));
  }, [data, isSearching, onOpenUser]);

  const filters: readonly FilterSpec[] = [
    {
      id: 'access',
      label: 'Access',
      value: search.entitlement,
      options: ACCESS_FILTER_OPTIONS,
      onChange: (value) => {
        onSearchChange({ ...search, entitlement: value as AiSpendSearch['entitlement'] });
      },
    },
  ];
  const isFiltered = isSearching || search.entitlement !== undefined;
  const onReset = () => {
    onSearchChange({ days: search.days, sort: search.sort });
  };

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load spend by user"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  /*
   * Kept data is shown while a new window loads, so every label reads the
   * window the numbers came from rather than the one just clicked.
   */
  const shownDays = data?.windowDays ?? search.days;
  const group =
    search.entitlement === undefined ? null : search.entitlement === 'premium' ? 'Premium' : 'Free';
  const plural = (count: number, one: string, many: string) =>
    `${count.toLocaleString('en-IN')} ${count === 1 ? one : many}`;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      <ScopePills
        label="Window"
        scopes={AI_SPEND_WINDOW_OPTIONS.map((days) => ({
          id: String(days),
          label: `Last ${String(days)} days`,
        }))}
        value={String(search.days)}
        onChange={(days) => {
          onSearchChange({ ...search, days: Number(days) });
        }}
      />

      <StatCards
        stats={[
          {
            label: `Model spend, ${String(shownDays)} days`,
            value: formatUsd(data?.totals.costUsd ?? null, data?.totals.costIsPartial ?? false),
            note: 'The whole bill',
          },
          {
            label: group === null ? 'Spent on people' : `Spent on ${group.toLowerCase()} users`,
            value: formatUsd(data?.attributed.costUsd ?? null, data?.attributed.costIsPartial ?? false),
            note:
              data === undefined
                ? ''
                : `${plural(data.attributed.users, 'person', 'people')}, ${plural(data.attributed.calls, 'call', 'calls')}.`,
          },
          {
            label: 'Belongs to nobody',
            value: formatUsd(data?.unattributed.costUsd ?? null, data?.unattributed.costIsPartial ?? false),
            note:
              data === undefined
                ? ''
                : /*
                   * Several sources fold into one number — admin tools,
                   * offline scripts, and the history of deleted accounts,
                   * whose calls lose their user when the account goes.
                   */
                  `${plural(data.unattributed.calls, 'call', 'calls')}: scripts, admin tools, deleted accounts`,
            borderClassName: 'border-warn-fg/25',
          },
          {
            label: 'Calls',
            value: (data?.totals.calls ?? 0).toLocaleString('en-IN'),
            note: `${formatTokens(data?.totals.inputTokens ?? 0)} in, ${formatTokens(data?.totals.outputTokens ?? 0)} out`,
          },
        ]}
      />

      <div className="mt-2">
        <TableToolbar
          searchValue={search.search ?? ''}
          searchPlaceholder="Search by name or phone"
          onSearchChange={(value) => {
            onSearchChange({ ...search, search: value.trim() === '' ? undefined : value.trim() });
          }}
          filters={filters}
          isFiltered={isFiltered}
          onReset={onReset}
          action={
            <ScopePills
              label="Ranked by"
              scopes={[
                { id: 'cost', label: 'By cost' },
                { id: 'calls', label: 'By calls' },
              ]}
              value={search.sort}
              onChange={(sort) => {
                onSearchChange({ ...search, sort });
              }}
            />
          }
        />
      </div>

      <DataTable
        caption="Who costs the most"
        title={isSearching ? 'Matching people' : 'Who costs the most'}
        count={
          data === undefined
            ? undefined
            : isSearching
              ? plural(rows.length, 'match', 'matches')
              : data.attributed.users > rows.length
                ? `top ${String(rows.length)} of ${data.attributed.users.toLocaleString('en-IN')} people`
                : plural(rows.length, 'person', 'people')
        }
        columns={COLUMNS}
        rows={rows}
        isLoading={query.isPending || query.isPlaceholderData}
        emptyTitle={isFiltered ? 'No one matches' : 'Nothing attributed in this window'}
        emptyBody={
          isFiltered
            ? 'Nobody with model spend in this window matches the search and filter.'
            : 'No model call in this window carried a user.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * A share as a length and a number. Costs in dollars are numbers to compare by
 * hand; the same costs as bars are read at a glance, and the figure stays
 * beside the bar because a bar cannot say "0.4%".
 */
function ShareCell({ share }: { readonly share: number | null }) {
  return (
    <span className="flex items-center justify-end gap-2">
      <span aria-hidden="true" className="h-[5px] w-11 overflow-hidden rounded-[3px] bg-secondary">
        <span
          className="block h-full rounded-[3px] bg-chart-1"
          style={{ width: `${String(Math.min(100, Math.round((share ?? 0) * 100)))}%` }}
        />
      </span>
      <span className="w-[42px] text-right font-mono text-xs text-muted-foreground">{percent(share)}</span>
    </span>
  );
}
